// ============= Imports =============
'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { useMsal } from '@azure/msal-react';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { isAuthBypassed } from '../authConfig';
import { tokenManager } from '../utils/tokenManager';
import { CurrentUser, LoggedInUser } from '../types';
import { currentUserPermissions } from '../apis/permissions/get/currentUserPermissions';
import { createLogger } from '../utils/logger';
import {
  getSpoofUserHeader,
  grantTemporaryOvertimeApprovalAccess,
} from '../utils/permissions';

export interface AuthContextProps {
  isAuthenticated: boolean;
  activeAccount: AccountInfo | null;
  accessToken: string | null;
  currentUser: CurrentUser | null;
  originalUser: CurrentUser | null;
  loggedInUser: LoggedInUser | null;
  spoofedUser?: { label: string; value: string; email: string } | null;
  setSpoofedUser?: (
    user: { label: string; value: string; email: string } | null
  ) => void;
  login: () => void;
  logout: () => void;
  getAccessToken: (
    scopes?: string[],
    options?: { skipStorageToken?: boolean }
  ) => Promise<string | null>;
}

interface AuthState {
  isAuthenticated: boolean;
  activeAccount: AccountInfo | null;
  accessToken: string | null;
  currentUser: CurrentUser | null;
  originalUser: CurrentUser | null; // Store original user permissions
  loggedInUser: LoggedInUser | null;
  spoofedUser: { label: string; value: string; email: string } | null;
}
// TODO: Implement token cache logic
// interface TokenCache {
//   [key: string]: {
//     token: string;
//     expiresOn: string;
//     scopes: string[];
//   };
// }

interface TokenResponse {
  token: string;
  expiresOn: Date;
}

// ============= Constants =============
const DEFAULT_SCOPES = ['openid', 'profile'];
const TOKEN_CACHE_KEY = 'tokenCache';
const TOKEN_EXPIRATION = 3600 * 1000; // 1 hour in milliseconds
const TOKEN_CHECK_INTERVAL = 60000; // Check every 30 seconds
const SPOOFED_USER_KEY = 'spoofedUser';

// ============= Logger =============
const logger = createLogger('AuthContext');

const readSpoofedUserFromStorage = (): {
  label: string;
  value: string;
  email: string;
} | null => {
  try {
    const raw = localStorage.getItem(SPOOFED_USER_KEY);
    return raw
      ? (JSON.parse(raw) as { label: string; value: string; email: string })
      : null;
  } catch {
    return null;
  }
};

const writeSpoofedUserToStorage = (
  user: { label: string; value: string; email: string } | null
): void => {
  try {
    if (user) localStorage.setItem(SPOOFED_USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(SPOOFED_USER_KEY);
  } catch {
    // ignore storage errors
  }
};

// ============= Context =============
const AuthContext = createContext<AuthContextProps | undefined>(undefined);

// ============= Hook =============
export const UseAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('UseAuth must be used within an AuthProvider');
  }
  return context;
};

// ============= Provider Component =============
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { instance, accounts, inProgress } = useMsal();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    activeAccount: null,
    accessToken: null,
    currentUser: null,
    originalUser: null,
    loggedInUser: null,
    spoofedUser: readSpoofedUserFromStorage(),
  });
  // TODO: Implement token cache logic
  // const getTokenCache = useCallback((): TokenCache => {
  //   try {
  //     return JSON.parse(sessionStorage.getItem(TOKEN_CACHE_KEY) || '{}');
  //   } catch {
  //     return {};
  //   }
  // }, []);

  // const setTokenCache = useCallback((cache: TokenCache): void => {
  //   sessionStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify(cache));
  // }, []);

  const clearTokenCache = useCallback((): void => {
    sessionStorage.removeItem(TOKEN_CACHE_KEY);
  }, []);

  const handleTokenError = useCallback(async () => {
    clearTokenCache();
    setAuthState((prev) => ({
      ...prev,
      isAuthenticated: false,
      activeAccount: null,
      accessToken: null,
      currentUser: null,
      loggedInUser: null,
    }));
    try {
      if (!isAuthBypassed) {
        await instance.loginRedirect({
          scopes: DEFAULT_SCOPES,
          prompt: 'select_account',
        });
      }
    } catch (e) {
      logger.error('Login redirect after token error failed', { error: e });
    }
  }, [clearTokenCache, instance]);

  const acquireToken = useCallback(
    async (
      account: AccountInfo,
      scopes: string[],
      forceRefresh = false
    ): Promise<TokenResponse | null> => {
      try {
        const response = await instance.acquireTokenSilent({
          account,
          scopes,
          forceRefresh,
        });

        const expiresOn =
          response.expiresOn || new Date(Date.now() + TOKEN_EXPIRATION);

        tokenManager.logTokenInfo(response.accessToken, scopes.join(', '));

        return {
          token: response.accessToken,
          expiresOn,
        };
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          try {
            const response = await instance.acquireTokenPopup({ scopes });
            return {
              token: response.accessToken,
              expiresOn:
                response.expiresOn || new Date(Date.now() + TOKEN_EXPIRATION),
            };
          } catch (popupError) {
            logger.error('Error acquiring token', { error: popupError });
            await handleTokenError();
            return null;
          }
        }
        logger.error('Error acquiring token silently', { error });
        await handleTokenError();
        return null;
      }
    },
    [instance, handleTokenError]
  );

  // handleTokenError is defined above
  // TODO: Implement refresh token logic
  // const refreshToken = useCallback(
  //   async (
  //     account: AccountInfo,
  //     scopes: string[],
  //     cacheKey: string
  //   ): Promise<void> => {
  //     const tokenResponse = await acquireToken(account, scopes, true);
  //     if (tokenResponse) {
  //       const tokenCache = getTokenCache();
  //       const newCache = {
  //         ...tokenCache,
  //         [cacheKey]: {
  //           token: tokenResponse.token,
  //           expiresOn: tokenResponse.expiresOn.toISOString(),
  //           scopes,
  //         },
  //       };
  //       setTokenCache(newCache);

  //       if (cacheKey === DEFAULT_SCOPES.sort().join('|')) {
  //         setAuthState((prev) => ({
  //           ...prev,
  //           accessToken: tokenResponse.token,
  //         }));
  //       }
  //     }
  //   },
  //   [acquireToken, getTokenCache, setTokenCache]
  // );

  const getAccessToken = useCallback(
    async (
      scopes?: string[],
      options: { skipStorageToken?: boolean } = {}
    ): Promise<string | null> => {
      if (isAuthBypassed) return 'mock-token';
      // Ensure we have an active account; if not, try to resolve one
      let account =
        authState.activeAccount || instance.getActiveAccount() || accounts[0];

      if (!account) {
        // Poll briefly for account readiness (e.g., immediately after redirect)
        const maxAttempts = 20;
        const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          const candidate =
            instance.getActiveAccount() ||
            instance.getAllAccounts()[0] ||
            accounts[0];
          if (candidate) {
            account = candidate;
            break;
          }
          // Wait 100ms before next check

          await delay(100);
        }
      }

      if (!account) {
        logger.warning('getAccessToken called before MSAL account was ready');
        return null;
      }

      // Align MSAL active account for future calls and update local state if needed
      try {
        instance.setActiveAccount(account);
      } catch {
        // ignore if MSAL version does not support setActiveAccount
      }
      if (
        !authState.activeAccount ||
        authState.activeAccount.homeAccountId !== account.homeAccountId
      ) {
        setAuthState((prev) => ({ ...prev, activeAccount: account }));
      }

      // Force skipStorageToken to true
      options.skipStorageToken = true;

      // Check if we should skip storage token check
      if (!options.skipStorageToken) {
        // Check if current MSAL token is valid
        if (tokenManager.validateMsalToken()) {
          const tokenData = tokenManager.getMsalTokenFromStorage();
          return tokenData?.secret || null;
        }
      }

      // If token is invalid, expired, or skip is true, acquire new token
      const scopesToUse = scopes || DEFAULT_SCOPES;
      const tokenResponse = await acquireToken(account, scopesToUse);

      if (tokenResponse) {
        return tokenResponse.token;
      }

      return null;
    },
    [authState.activeAccount, acquireToken, instance, accounts]
  );

  // Use refs to track values and prevent unnecessary re-fetches
  const hasFetchedUserRef = useRef(false);
  const lastSpoofedUserRef = useRef<string | null>(null);
  const lastAccountIdRef = useRef<string | null>(null);
  const getAccessTokenRef = useRef(getAccessToken);

  // Keep ref updated with latest getAccessToken
  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  }, [getAccessToken]);

  const fetchCurrentUser = useCallback(async () => {
    // Only proceed if we have an active account or are in bypass mode
    if (!authState.activeAccount && !isAuthBypassed) {
      logger.debug('No active account available, skipping user fetch');
      return;
    }

    if (isAuthBypassed) {
      // Mock user data for bypass mode
      const mockUser: CurrentUser = grantTemporaryOvertimeApprovalAccess({
        userId: 'mock-user-id',
        userDisplayName: 'Mock User',
        userEmail: 'mockuser@domain.com',
        permissions: [],
        adGroups: [],
        canViewAllWorkflows: true,
        canCreateWorkflowTypes: [
          'DubaiPaymentApproval',
          'DubaiTransferRequisition',
          'DubaiPaymentFromOfficeAcc',
        ],
        canDeleteAnyWorkflow: true,
        canManagePermissions: true,
        testingOverrideEnabled: false,
      });
      setAuthState((prev) => ({
        ...prev,
        currentUser: mockUser,
        originalUser: prev.originalUser || mockUser,
      }));
      return;
    }

    try {
      // Always fetch original user permissions first (without spoofing)
      const originalResponse = await currentUserPermissions({
        getAccessToken: getAccessTokenRef.current,
        spoofedUser: null,
      });

      if (originalResponse.success && originalResponse.data) {
        // TEMPORARY: Overtime Approval backend permissions are not ready yet.
        const originalUserData = grantTemporaryOvertimeApprovalAccess(
          originalResponse.data as CurrentUser
        );

        // If we're spoofing, fetch the spoofed user's permissions too
        if (authState.spoofedUser?.value || authState.spoofedUser?.email) {
          const spoofedResponse = await currentUserPermissions({
            getAccessToken: getAccessTokenRef.current,
            spoofedUser: getSpoofUserHeader(authState.spoofedUser),
          });

          if (spoofedResponse.success && spoofedResponse.data) {
            const spoofedUserData = grantTemporaryOvertimeApprovalAccess(
              spoofedResponse.data as CurrentUser
            );
            setAuthState((prev) => ({
              ...prev,
              currentUser: spoofedUserData,
              originalUser: originalUserData,
            }));
            logger.debug('Spoofed user data fetched and set', {
              data: spoofedResponse.data,
            });
          } else {
            logger.error('Failed to fetch spoofed user', {
              error: spoofedResponse.error,
            });
            // Fallback to original user if spoofed user fetch fails
            setAuthState((prev) => ({
              ...prev,
              currentUser: originalUserData,
              originalUser: originalUserData,
            }));
          }
        } else {
          // Not spoofing, use original user data for both
          setAuthState((prev) => ({
            ...prev,
            currentUser: originalUserData,
            originalUser: originalUserData,
          }));
        }
        logger.debug('Original user data fetched and set', {
          data: originalResponse.data,
        });
      } else {
        logger.error('Failed to fetch original user', {
          error: originalResponse.error,
        });
      }
    } catch (error) {
      logger.error('Error fetching current user', { error });
    }
  }, [authState.activeAccount, authState.spoofedUser?.value]);

  const login = async () => {
    if (isAuthBypassed) {
      logger.debug('Bypass mode: Login not required');
      return;
    }
    try {
      await instance.loginRedirect({
        scopes: DEFAULT_SCOPES,
        prompt: 'select_account',
      });
    } catch (error) {
      logger.error('Login failed', { error });
    }
  };

  const logout = async () => {
    if (isAuthBypassed) {
      logger.debug('Bypass mode: Logout not required');
      return;
    }
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      logger.error('Logout failed', { error });
    } finally {
      setAuthState(() => ({
        isAuthenticated: false,
        activeAccount: null,
        accessToken: null,
        currentUser: null,
        loggedInUser: null,
        spoofedUser: null,
        originalUser: null,
      }));
      clearTokenCache();
      // Reset fetch tracking on logout
      hasFetchedUserRef.current = false;
      lastSpoofedUserRef.current = null;
      lastAccountIdRef.current = null;
    }
  };

  useEffect(() => {
    if (!authState.isAuthenticated) return;

    const checkToken = () => {
      const isValid = tokenManager.checkAndHandleTokenExpiration();
      if (!isValid) {
        // Token was expired and cleared, trigger new token acquisition
        void getAccessToken();
      }
    };

    // Check immediately
    checkToken();

    // Set up interval to check periodically
    const interval = setInterval(checkToken, TOKEN_CHECK_INTERVAL); // Check every 60 seconds

    return () => clearInterval(interval);
  }, [authState.isAuthenticated, getAccessToken]);

  const deriveLoggedInUser = (account: AccountInfo): LoggedInUser => {
    const claims = account.idTokenClaims as Record<string, unknown> | undefined;
    const name = (claims?.name as string) || undefined;
    const oid = (claims?.oid as string) || undefined;
    return {
      userId: oid || account.localAccountId,
      userDisplayName: name || account.name || account.username,
      userEmail: account.username,
    };
  };

  useEffect(() => {
    if (isAuthBypassed) {
      const mockAccount: AccountInfo = {
        homeAccountId: 'mock-home-account-id',
        environment: 'mock-environment',
        tenantId: 'mock-tenant-id',
        username: 'mockuser@domain.com',
        localAccountId: 'mock-local-id',
        idTokenClaims: {
          name: 'Mock User',
          oid: 'mock-oid',
        },
      };
      setAuthState((prev) => ({
        ...prev,
        isAuthenticated: true,
        activeAccount: mockAccount,
        accessToken: 'mock-token',
        currentUser: null,
        loggedInUser: {
          userId: 'mock-oid',
          userDisplayName: 'Mock User',
          userEmail: 'mockuser@domain.com',
        },
      }));
      return;
    }

    const currentAccount = accounts[0];

    const updateAuthState = async () => {
      if (currentAccount && inProgress === 'none') {
        const tokenResponse = await acquireToken(
          currentAccount,
          DEFAULT_SCOPES
        );
        setAuthState((prev) => ({
          ...prev,
          isAuthenticated: true,
          activeAccount: currentAccount,
          accessToken: tokenResponse?.token || null,
          currentUser: null,
          loggedInUser: deriveLoggedInUser(currentAccount),
        }));
      } else if (!currentAccount && inProgress === 'none') {
        setAuthState((prev) => ({
          ...prev,
          isAuthenticated: false,
          activeAccount: null,
          accessToken: null,
          currentUser: null,
          loggedInUser: null,
        }));
      }
    };

    void updateAuthState();
  }, [accounts, inProgress, instance, acquireToken]);

  // Fetch current user (or spoofed user) when authentication or spoof changes
  useEffect(() => {
    const accountId = authState.activeAccount?.homeAccountId;

    if (!authState.isAuthenticated || !accountId) {
      // Reset fetch tracking when not authenticated
      if (hasFetchedUserRef.current) {
        hasFetchedUserRef.current = false;
        lastSpoofedUserRef.current = null;
        lastAccountIdRef.current = null;
      }
      return;
    }

    const currentSpoofedUser = getSpoofUserHeader(authState.spoofedUser);
    const accountChanged = lastAccountIdRef.current !== accountId;
    const spoofedUserChanged =
      lastSpoofedUserRef.current !== currentSpoofedUser;

    // Fetch if we haven't fetched yet, or if account/spoofed user changed
    const shouldFetch =
      !hasFetchedUserRef.current || accountChanged || spoofedUserChanged;

    if (shouldFetch) {
      hasFetchedUserRef.current = true;
      lastAccountIdRef.current = accountId;
      lastSpoofedUserRef.current = currentSpoofedUser;
      void fetchCurrentUser();
    }
  }, [
    authState.isAuthenticated,
    authState.activeAccount?.homeAccountId, // Only track account ID, not the whole object
    authState.spoofedUser?.value,
    authState.spoofedUser?.email,
    fetchCurrentUser,
  ]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: authState.isAuthenticated,
        activeAccount: authState.activeAccount,
        accessToken: authState.accessToken,
        currentUser: authState.currentUser,
        originalUser: authState.originalUser,
        loggedInUser: authState.loggedInUser,
        spoofedUser: authState.spoofedUser,
        setSpoofedUser: (user) => {
          writeSpoofedUserToStorage(user);
          setAuthState((prev) => ({ ...prev, spoofedUser: user }));
        },
        login,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
